/* ============================================================
   app.jsx — shell: routing, auth, tweaks, toast
   ============================================================ */

import api from "./api/api.js";
import oauth from "./api/api_external.js";
import { mergeImageItems, normalizeImageItem, normalizeImageItems } from "./api/image-view-model.js";
import CONFIG from "./config/config.js";
import ENDPOINTS from "./config/endpoints.js";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "galleryLayout": "masonry",
  "genLayout": "focus",
  "density": "regular",
  "cardRadius": 16,
  "theme": "system"
}/*EDITMODE-END*/;

const DENSITY_MIN = { compact: "168px", regular: "220px", comfy: "280px" };
const THEME_STORAGE_KEY = "prompto-theme";
const GENERATE_SEED_PROMPT_KEY = "prompto.generate.seedPrompt";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function getSystemTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function App() {
  const queryClientApi = useQueryClient();
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const initialRoute = React.useMemo(() => parsePath(window.location.pathname), []);
  const [galleryItems, setGalleryItems] = React.useState(() => (
    CONFIG.USE_MOCK ? GALLERY.map((item, index) => normalizeImageItem(item, index)) : []
  ));
  const galleryItemsRef = React.useRef(galleryItems);
  const [route, setRoute] = React.useState(initialRoute.name);
  const [mypageTab, setMypageTab] = React.useState(initialRoute.tab || "created");
  const [current, setCurrent] = React.useState(() => (
    initialRoute.name === "detail" ? findRouteItem(galleryItemsRef.current, initialRoute.itemId) : null
  ));
  const [currentID, setCurrentID] = React.useState(() => initialRoute.name === "detail" ? initialRoute.itemId : null);
  const [auth, setAuth] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [liked, setLiked] = React.useState(() => new Set());
  const [saved, setSaved] = React.useState(() => new Set());
  const [toastMsg, setToastMsg] = React.useState(null);
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [seedPrompt, setSeedPrompt] = React.useState("");
  const [lang, setLang] = React.useState(() => {
    try { return localStorage.getItem("prompto-lang") || "ko"; } catch (e) { return "ko"; }
  });
  const [themePref, setThemePrefState] = React.useState(() => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return saved === "light" || saved === "dark" ? saved : null;
    } catch (e) {
      return null;
    }
  });
  const [systemTheme, setSystemTheme] = React.useState(() => getSystemTheme());
  const scrollPos = React.useRef({});
  const tx = React.useCallback((k, v) => tr(lang, k, v), [lang]);

  React.useEffect(() => {
    galleryItemsRef.current = galleryItems;
  }, [galleryItems]);

  const rememberImages = React.useCallback((items) => {
    const normalized = normalizeImageItems(items);
    if (!normalized.length) return;
    setGalleryItems((prev) => mergeImageItems(prev, normalized));
    setLiked((prev) => syncFlagSet(prev, normalized, "liked"));
    setSaved((prev) => syncFlagSet(prev, normalized, "saved"));
  }, []);

  const findKnownItem = React.useCallback((itemId) => (
    findRouteItem(galleryItemsRef.current, itemId) || (CONFIG.USE_MOCK ? findRouteItem(GALLERY, itemId) : null)
  ), []);

  React.useEffect(() => {
    try { localStorage.setItem("prompto-lang", lang); } catch (e) {}
    document.documentElement.lang = lang;
  }, [lang]);

  const authQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const user = await api.auth.me();
      return user ? normalizeUser(user) : null;
    },
    initialData: () => {
      const cached = api.auth.cachedUser?.();
      return cached ? normalizeUser(cached) : undefined;
    },
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (typeof authQuery.data !== "undefined") setAuth(authQuery.data);
    else if (authQuery.isError) setAuth(null);
  }, [authQuery.data, authQuery.isError]);

  const applyRoute = React.useCallback((target, { scroll = true } = {}) => {
    const requestedID = target.item?.id || target.itemId || null;
    const item = target.name === "detail" ? (target.item ? normalizeImageItem(target.item) : findKnownItem(requestedID)) : null;
    if (target.name === "detail") {
      if (!item && !requestedID) {
        setRoute("gallery");
        setCurrent(null);
        setCurrentID(null);
        if (scroll) window.scrollTo({ top: 0 });
        return { name: "gallery", item: null };
      }
      setCurrentID(requestedID || item.id);
      setCurrent(item);
      setRoute("detail");
      if (scroll) window.scrollTo({ top: 0 });
      return { name: "detail", item: item || { id: requestedID } };
    }
    if (target.name === "mypage") setMypageTab(target.tab || "created");
    setCurrentID(null);
    setRoute(target.name);
    if (scroll) window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    return { name: target.name, item: null, tab: target.name === "mypage" ? (target.tab || "created") : null };
  }, [findKnownItem]);

  const navigate = React.useCallback((target, opts = {}) => {
    const applied = applyRoute(target, opts);
    const path = routeToPath(applied);
    const state = { route: applied.name, itemId: applied.item?.id || null, tab: applied.tab || null };
    const method = opts.replace ? "replaceState" : "pushState";
    if (opts.replace || window.location.pathname !== path) {
      window.history[method](state, "", path);
    }
  }, [applyRoute]);

  React.useEffect(() => {
    const applied = applyRoute(parsePath(window.location.pathname), { scroll: false });
    window.history.replaceState({ route: applied.name, itemId: applied.item?.id || null }, "", routeToPath(applied));

    const onPopState = () => {
      applyRoute(parsePath(window.location.pathname), { scroll: false });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [applyRoute]);

  const detailQuery = useQuery({
    queryKey: ["images", "detail", currentID],
    queryFn: () => api.images.get(currentID),
    enabled: route === "detail" && Boolean(currentID),
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (route !== "detail" || !currentID) return;
    const known = findKnownItem(currentID);
    if (known) setCurrent((prev) => (!prev || prev.id !== known.id ? known : prev));
    if (detailQuery.data) {
      const normalized = normalizeImageItem(detailQuery.data);
      setCurrent(normalized);
      rememberImages([normalized]);
      return;
    }
    if (detailQuery.isError && !known) {
      setRoute("gallery");
      setCurrent(null);
      setCurrentID(null);
      window.history.replaceState({ route: "gallery", itemId: null }, "", "/");
    }
  }, [route, currentID, findKnownItem, rememberImages, detailQuery.data, detailQuery.isError]);

  // apply density + radius
  React.useEffect(() => {
    const r = document.documentElement;
    r.style.setProperty("--grid-min", DENSITY_MIN[t.density] || DENSITY_MIN.regular);
    r.style.setProperty("--r-md", (t.cardRadius || 16) + "px");
  }, [t.density, t.cardRadius]);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = (event) => setSystemTheme(event?.matches ? "dark" : (mq.matches ? "dark" : "light"));
    sync();
    mq.addEventListener ? mq.addEventListener("change", sync) : mq.addListener(sync);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", sync) : mq.removeListener(sync); };
  }, []);

  const theme = themePref || systemTheme;
  const setTheme = React.useCallback((value) => {
    const next = value === "system" ? null : value;
    setThemePrefState(next === "light" || next === "dark" ? next : null);
  }, []);

  React.useEffect(() => {
    const r = document.documentElement;
    if (theme === "dark") r.setAttribute("data-theme", "dark");
    else r.removeAttribute("data-theme");
  }, [theme]);

  React.useEffect(() => {
    try {
      if (themePref) localStorage.setItem(THEME_STORAGE_KEY, themePref);
      else localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (e) {}
  }, [themePref]);

  const go = (target, opts) => navigate(typeof target === "string" ? { name: target } : target, opts);
  const open = (item) => navigate({ name: "detail", item });

  const toast = (msg) => { setToastMsg(msg); window.clearTimeout(toast._t); toast._t = window.setTimeout(() => setToastMsg(null), 2200); };

  const applyImagePatch = React.useCallback((id, patch) => {
    if (!id || !patch) return;
    setGalleryItems((prev) => prev.map((item) => item.id === id ? { ...item, ...patch } : item));
    setCurrent((prev) => prev?.id === id ? { ...prev, ...patch } : prev);
    if (typeof patch.liked === "boolean") {
      setLiked((prev) => setFlag(prev, id, patch.liked));
    }
    if (typeof patch.saved === "boolean") {
      setSaved((prev) => setFlag(prev, id, patch.saved));
    }
  }, []);

  const getImageFlag = React.useCallback((id, key, setValue) => {
    const item = current?.id === id ? current : findKnownItem(id);
    return setValue.has(id) || Boolean(item?.[key]);
  }, [current, findKnownItem]);

  const onLike = async (id) => {
    if (!CONFIG.FEATURES.social) return;
    const wasLiked = getImageFlag(id, "liked", liked);
    try {
      const res = wasLiked ? await api.images.unlike(id) : await api.images.like(id);
      const patch = { liked: Boolean(res?.liked ?? !wasLiked) };
      if (typeof res?.likes === "number") patch.likes = res.likes;
      applyImagePatch(id, patch);
    } catch (e) {
      toast(e.message || "Like failed");
    }
  };

  const onSave = async (id) => {
    if (!CONFIG.FEATURES.social) return;
    const wasSaved = getImageFlag(id, "saved", saved);
    try {
      const res = wasSaved ? await api.images.unsave(id) : await api.images.save(id);
      const patch = { saved: Boolean(res?.saved ?? !wasSaved) };
      if (typeof res?.saves === "number") patch.saves = res.saves;
      applyImagePatch(id, patch);
    } catch (e) {
      toast(e.message || "Save failed");
    }
  };

  const onDownload = async (id, { resolution = "hi" } = {}) => {
    if (!CONFIG.FEATURES.download) return;
    try {
      triggerDownload(apiPath(ENDPOINTS.images.file(id), { resolution }), `promzi-${id}.png`);
      toast(resolution === "hi" ? tx("t_downloaded_hi") : tx("t_downloaded"));
    } catch (e) {
      toast(e.message || "Download failed");
    }
  };

  const onAdminDelete = async (id) => {
    try {
      await api.images.remove(id);
      setGalleryItems((prev) => prev.filter((item) => item.id !== id));
      queryClientApi.removeQueries({ queryKey: ["images", "detail", id] });
      queryClientApi.invalidateQueries({ queryKey: ["gallery", "list"] });
      go("gallery");
      toast(tx("t_admin_deleted"));
    } catch (e) {
      toast(tx("admin_delete_failed"));
    }
  };

  const onLogin = () => setLoginOpen(true);
  const doLogin = async (credentials) => {
    let user = await api.auth.login(credentials);
    if (!user) user = await api.auth.me();
    user = normalizeUser(user);
    queryClientApi.setQueryData(["auth", "me"], user);
    setAuth(user);
    setLoginOpen(false);
    toast(tx("t_welcome", { name: lang === "en" ? user.nameEn : user.name }));
  };
  const doRegister = async (credentials) => {
    let user = await api.auth.register(credentials);
    if (!user) user = await api.auth.me();
    user = normalizeUser(user);
    queryClientApi.setQueryData(["auth", "me"], user);
    setAuth(user);
    setLoginOpen(false);
    toast(tx("t_welcome", { name: lang === "en" ? user.nameEn : user.name }));
  };
  const onLogout = async () => {
    await api.auth.logout();
    queryClientApi.setQueryData(["auth", "me"], null);
    queryClientApi.removeQueries({ queryKey: ["users"] });
    setAuth(null);
    toast(tx("t_loggedout"));
    go("gallery");
  };

  const startPrompt = (p) => {
    if (!CONFIG.FEATURES.generation) {
      toast(lang === "en" ? "Generation is currently unavailable." : "현재 생성 기능을 사용할 수 없어요.");
      return;
    }
    try {
      if (p) window.sessionStorage.setItem(GENERATE_SEED_PROMPT_KEY, p);
      else window.sessionStorage.removeItem(GENERATE_SEED_PROMPT_KEY);
    } catch (e) {}
    setSeedPrompt(p);
    navigate({ name: "generate" });
  };

  React.useEffect(() => {
    if (route === "generate" && !CONFIG.FEATURES.generation) {
      navigate({ name: "gallery" }, { replace: true });
    }
  }, [route, navigate]);

  return (
    <LangContext.Provider value={lang}>
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <Header route={route} go={go} open={open} auth={auth} onLogin={onLogin} onLogout={onLogout} q={q} setQ={setQ} lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} toast={toast} items={galleryItems} />

      <main style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {route === "gallery" && <Gallery go={go} open={open} q={q} setQ={setQ} liked={liked} onLike={onLike} layout={t.galleryLayout} startPrompt={startPrompt} items={galleryItems} onItemsLoaded={rememberImages} theme={theme} toast={toast} />}
        {route === "detail" && <Detail item={current} go={go} open={open} liked={liked} saved={saved} onLike={onLike} onSave={onSave} onDownload={onDownload} startPrompt={startPrompt} toast={toast} auth={auth} onAdminDelete={onAdminDelete} />}
        {route === "generate" && <Generate auth={auth} onLogin={onLogin} go={go} open={open} liked={liked} onLike={onLike} onDownload={onDownload} toast={toast} seedPrompt={seedPrompt} genLayout={t.genLayout} />}
        {route === "upload" && <Upload auth={auth} onLogin={onLogin} go={go} toast={toast} />}
        {route === "mypage" && <MyPage auth={auth} onLogin={onLogin} go={go} open={open} liked={liked} onLike={onLike} layout={t.galleryLayout} onItemsLoaded={rememberImages} initialTab={mypageTab} />}
        {route === "settings" && <SettingsPage auth={auth} onLogin={onLogin} go={go} lang={lang} setLang={setLang} theme={themePref || "system"} setTheme={setTheme} onLogout={onLogout} />}
      </main>

      {/* toast */}
      {toastMsg && (
        <div role="status" aria-live="polite" style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", zIndex: 200,
          animation: "toastIn .3s var(--ease-bounce)",
          background: "color-mix(in srgb, var(--paper) 94%, var(--k-bg) 6%)", color: "var(--ink)",
          padding: "13px 22px", borderRadius: "var(--r-md)",
          fontSize: 14, fontWeight: 600, boxShadow: "var(--sh-3)", display: "flex", alignItems: "center", gap: 10,
          border: "1px solid var(--line-2)", borderLeft: "3px solid var(--k-yellow)",
          backdropFilter: "saturate(180%) blur(12px)", WebkitBackdropFilter: "saturate(180%) blur(12px)",
          maxWidth: "calc(100vw - 32px)",
        }}>
          <Icon name="check" size={18} stroke={2.4} style={{ color: "var(--k-yellow-deep)" }} /> {toastMsg}
        </div>
      )}

      {/* login modal */}
      {loginOpen && <LoginModal theme={theme} onClose={() => setLoginOpen(false)} onLogin={doLogin} onRegister={doRegister} />}

      {/* Tweaks */}
      <TweaksPanel>
        <TweakSection label={lang === "en" ? "Style" : "스타일"} />
        <TweakSlider label={lang === "en" ? "Card radius" : "카드 모서리"} value={t.cardRadius} min={4} max={24} step={2} unit="px"
          onChange={(v) => setTweak("cardRadius", v)} />
        <TweakRadio label={lang === "en" ? "Theme" : "테마"} value={t.theme}
          options={lang === "en"
            ? [{ value: "system", label: "System" }, { value: "light", label: "Light" }, { value: "dark", label: "Dark" }]
            : [{ value: "system", label: "시스템" }, { value: "light", label: "화이트" }, { value: "dark", label: "다크" }]}
          onChange={(v) => setTweak("theme", v)} />
      </TweaksPanel>
    </div>
    </LangContext.Provider>
  );
}

function SettingsPage({ auth, onLogin, go, lang, setLang, theme, setTheme, onLogout }) {
  const { t } = useT();
  if (!auth) {
    return <LoginGate onLogin={onLogin} go={go} title={t("settings_login_title")} desc={t("settings_login_desc")} primaryLabel={t("login")} icon="settings" primaryIcon={null} features={false} />;
  }
  const display = displayUserName(auth);
  return (
    <div className="container" style={{ padding: "34px 32px 72px", maxWidth: 760 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <Avatar author={auth} size={56} />
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow">Account</div>
          <h1 style={{ fontSize: 30, marginTop: 4 }}>{t("settings")}</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2 }}>{display} · @{auth.handle || "me"}</p>
        </div>
      </div>

      <section className="card" style={{ padding: 20, borderRadius: "var(--r-md)", marginBottom: 14 }}>
        <h2 style={{ fontSize: 17, marginBottom: 14 }}>{t("settings_profile")}</h2>
        <SettingRow label={t("settings_display_name")} value={display} />
        <SettingRow label={t("settings_handle")} value={`@${auth.handle || "me"}`} />
        <SettingRow label={t("settings_email")} value={auth.email || "-"} />
        <ApiKeysSection />
      </section>

      <section className="card" style={{ padding: 20, borderRadius: "var(--r-md)", marginBottom: 14 }}>
        <h2 style={{ fontSize: 17, marginBottom: 14 }}>{t("settings_preferences")}</h2>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "10px 0" }}>
          <span style={{ fontWeight: 700 }}>{t("settings_language")}</span>
          <div className="lang-seg" role="group" aria-label="Language">
            <button data-on={lang === "ko"} onClick={() => setLang("ko")}>KOR</button>
            <button data-on={lang === "en"} onClick={() => setLang("en")}>ENG</button>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "10px 0" }}>
          <span style={{ fontWeight: 700 }}>{t("settings_theme")}</span>
          <div className="lang-seg" role="group" aria-label="Theme">
            {[["system", t("theme_system")], ["light", t("theme_light")], ["dark", t("theme_dark")]].map(([value, label]) => (
              <button key={value} data-on={theme === value} onClick={() => setTheme(value)}>{label}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="card" style={{ padding: 20, borderRadius: "var(--r-md)" }}>
        <button className="btn btn-line" onClick={() => go({ name: "mypage", tab: "created" })} style={{ marginRight: 8 }}>{t("menu_mywork")}</button>
        <button className="btn btn-line" onClick={onLogout}>{t("logout")}</button>
      </section>
    </div>
  );
}

function SettingRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 18, padding: "10px 0", borderTop: "1px solid var(--line)" }}>
      <span className="muted" style={{ fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: 700, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function ApiKeysSection() {
  const { t } = useT();
  const queryClient = useQueryClient();
  const keysQuery = useQuery({ queryKey: ["apiKeys"], queryFn: () => api.apiKeys.list() });
  const [formOpen, setFormOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [createdKey, setCreatedKey] = React.useState(null); // { name, key } — 1회 표시
  const [copied, setCopied] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const items = keysQuery.data?.items || [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["apiKeys"] });

  const create = async (event) => {
    event?.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.apiKeys.create({ name: name.trim() });
      setCreatedKey({ name: res.name, key: res.key });
      setName("");
      setFormOpen(false);
      refresh();
    } catch (e) {
      setError(t("api_keys_error"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("api_keys_revoke_confirm"))) return;
    setError("");
    try {
      await api.apiKeys.revoke(id);
      refresh();
    } catch (e) {
      setError(t("api_keys_error"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : t("api_keys_never_used"));

  return (
    <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14, marginTop: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t("settings_api_keys")}</div>
          <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t("api_keys_desc")}</p>
        </div>
        {!formOpen && !createdKey && (
          <button className="btn btn-line" style={{ flexShrink: 0 }} onClick={() => setFormOpen(true)} disabled={items.length >= 5}>
            {t("api_keys_new")}
          </button>
        )}
      </div>

      {(error || keysQuery.isError) && (
        <p style={{ color: "var(--danger, #d33)", fontSize: 13, marginTop: 8 }}>{error || t("api_keys_error")}</p>
      )}

      {formOpen && (
        <form onSubmit={create} style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <input
            type="text" value={name} maxLength={100} autoFocus
            placeholder={t("api_keys_name_ph")}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: 1, padding: "8px 12px", borderRadius: "var(--r-sm, 8px)", border: "1px solid var(--line-2)", background: "var(--paper)", color: "var(--ink)", fontSize: 14 }}
          />
          <button className="btn btn-spark" type="submit" disabled={!name.trim() || busy}>{t("api_keys_create")}</button>
          <button className="btn btn-line" type="button" onClick={() => { setFormOpen(false); setName(""); }}>{t("api_keys_cancel")}</button>
        </form>
      )}

      {createdKey && (
        <div style={{ marginTop: 12, padding: 14, borderRadius: "var(--r-sm, 8px)", border: "1px solid var(--line-2)", background: "color-mix(in srgb, var(--k-yellow) 8%, var(--paper))" }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{createdKey.name}</div>
          <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{t("api_keys_created_notice")}</p>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={{ flex: 1, fontSize: 12, padding: "8px 10px", borderRadius: 6, background: "var(--paper)", border: "1px solid var(--line)", wordBreak: "break-all", userSelect: "all" }}>
              {createdKey.key}
            </code>
            <button className="btn btn-line" type="button" onClick={copy}>{copied ? t("api_keys_copied") : t("api_keys_copy")}</button>
            <button className="btn btn-spark" type="button" onClick={() => setCreatedKey(null)}>{t("api_keys_done")}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {items.length === 0 && !keysQuery.isLoading && !keysQuery.isError && (
          <p className="muted" style={{ fontSize: 13, padding: "8px 0" }}>{t("api_keys_empty")}</p>
        )}
        {items.map((key) => (
          <div key={key.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid var(--line)" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{key.name}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                <code>{key.keyPrefix}…</code>
                {" · "}{t("api_keys_created_at")} {formatDate(key.createdAt)}
                {" · "}{t("api_keys_last_used")} {formatDate(key.lastUsedAt)}
              </div>
            </div>
            <button className="btn btn-line" style={{ flexShrink: 0 }} onClick={() => revoke(key.id)}>{t("api_keys_revoke")}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function normalizeUser(user) {
  const fallback = { name: "김디자인", nameEn: "Designer Kim", handle: "designer" };
  if (!user) return fallback;
  const display = user.name || user.displayName || user.nickname || user.email || fallback.name;
  return {
    ...user,
    name: display,
    nameEn: user.nameEn || user.displayNameEn || display,
    handle: user.handle || user.username || (user.email ? user.email.split("@")[0] : fallback.handle),
  };
}

function syncFlagSet(previous, items, key) {
  const next = new Set(previous);
  for (const item of items) {
    if (!item?.id || typeof item[key] !== "boolean") continue;
    if (item[key]) next.add(item.id); else next.delete(item.id);
  }
  return next;
}

function setFlag(previous, id, value) {
  const next = new Set(previous);
  if (value) next.add(id); else next.delete(id);
  return next;
}

async function downloadURL(url, filename) {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, filename);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  } catch (e) {
    triggerDownload(url, filename);
  }
}

function apiPath(path, query = {}) {
  const base = CONFIG.API_BASE_URL || "";
  const url = new URL(`${base}${path}`, window.location.origin);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, value);
  });
  return url.pathname + url.search;
}

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "promzi-image.png";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function LoginModal({ theme, onClose, onLogin, onRegister }) {
  const { t } = useT();
  const [mode, setMode] = React.useState("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const setAuthMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    if (nextMode === "register") {
      setEmail("");
      setPassword("");
    }
  };
  const submit = async (event) => {
    event?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const action = mode === "register" ? onRegister : onLogin;
      await action({ email, password });
    } catch (e) {
      setError(e.message || (mode === "register" ? t("lm_register_failed") : t("lm_login_failed")));
      setLoading(false);
    }
  };
  const startOAuth = (provider) => {
    setError("");
    try {
      oauth[provider].startRedirect();
    } catch (e) {
      setError(e.message || "OAuth login failed");
    }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={t("lm_title")} style={{ position: "fixed", inset: 0, zIndex: 150, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.6)", animation: "fadeUp .2s ease" }} />
      <div className="card" style={{ position: "relative", width: "100%", maxWidth: 400, padding: 32, borderRadius: "var(--r-xl)",
        boxShadow: "var(--sh-pop)", animation: "popIn .3s var(--ease-out)" }}>
        <button className="btn-icon btn-ghost" onClick={onClose} style={{ position: "absolute", top: 14, right: 14 }} aria-label={t("close")}>
          <Icon name="x" size={20} />
        </button>
        <div style={{ textAlign: "center" }}>
          <Logo size={24} theme={theme} />
          <h2 style={{ fontSize: 23, marginTop: 18 }}>{t("lm_title")}</h2>
          <p className="muted" style={{ marginTop: 8, fontSize: 14.5 }}>{t("lm_sub")}</p>
        </div>
        <div role="tablist" aria-label={t("lm_title")} style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 4, marginTop: 24,
          border: "1px solid var(--line-2)", borderRadius: "var(--r-md)", background: "var(--surface-2)",
        }}>
          {[
            ["login", t("lm_login_tab")],
            ["register", t("lm_register_tab")],
          ].map(([value, label]) => {
            const active = mode === value;
            return (
              <button key={value} type="button" role="tab" aria-selected={active} onClick={() => setAuthMode(value)}
                style={{
                  height: 40, border: active ? "1px solid var(--line)" : "1px solid transparent", borderRadius: "calc(var(--r-md) - 4px)", cursor: "pointer",
                  fontFamily: "inherit", fontWeight: 800, color: active ? "var(--ink)" : "var(--ink-3)",
                  background: active ? "var(--paper)" : "transparent", boxShadow: active ? "var(--sh-1)" : "none",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "background .16s var(--ease), color .16s var(--ease), border-color .16s var(--ease), box-shadow .16s var(--ease)",
                }} onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "var(--surface)";
                }} onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}>
                {label}
              </button>
            );
          })}
        </div>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
          <input className="login-input" placeholder={t("lm_email")} value={email} onChange={(e) => setEmail(e.target.value)}
            style={loginInput} onFocus={(e) => e.target.style.borderColor = "var(--accent)"} onBlur={(e) => e.target.style.borderColor = "var(--line-2)"} />
          <input className="login-input" placeholder={t("lm_pw")} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            style={loginInput} onFocus={(e) => e.target.style.borderColor = "var(--accent)"} onBlur={(e) => e.target.style.borderColor = "var(--line-2)"} />
          {error && <div role="alert" style={{ color: "var(--danger, #d33)", fontSize: 13, fontWeight: 700 }}>{error}</div>}
          <button type="submit" className="btn btn-spark btn-lg" disabled={loading} style={{ width: "100%", marginTop: 4, opacity: loading ? .65 : 1 }}>
            {loading ? "..." : t(mode === "register" ? "lm_register" : "lm_login")}
          </button>
        </form>
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
          <hr className="divider" style={{ flex: 1 }} /><span className="muted" style={{ fontSize: 12.5 }}>{t("lm_or")}</span><hr className="divider" style={{ flex: 1 }} />
        </div>
        {(CONFIG.FEATURES.oauthGoogle || CONFIG.FEATURES.oauthKakao) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              CONFIG.FEATURES.oauthGoogle ? [t("lm_continue_email"), "user", "google"] : null,
              CONFIG.FEATURES.oauthKakao ? [t("lm_continue_kakao"), "globe", "kakao"] : null,
            ].filter(Boolean).map(([l, icon, provider]) => (
              <button key={provider} className="btn btn-line" onClick={() => startOAuth(provider)} disabled={loading} style={{ width: "100%" }}>
                <Icon name={icon} size={18} style={{ color: "var(--ink-3)" }} /> {l}
              </button>
            ))}
          </div>
        )}
        <p className="muted" style={{ fontSize: 11.5, textAlign: "center", marginTop: 18, lineHeight: 1.6 }}>
          {t("lm_terms")}
        </p>
      </div>
    </div>
  );
}

const loginInput = {
  height: 48, padding: "0 14px", borderRadius: "var(--r-md)", border: "1px solid var(--line-2)",
  fontSize: 15, fontFamily: "inherit", outline: "none", background: "var(--surface-2)", color: "var(--ink)",
  caretColor: "var(--ink)", boxShadow: "inset 0 1px 0 color-mix(in srgb, var(--k-text) 4%, transparent)", transition: "border-color .15s, background .15s, color .15s",
};

ReactDOM.createRoot(document.getElementById("root")).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);
